"use client";

import { useEffect } from "react";
import { setSessionRecordingConsent } from "@/features/analytics/lib/session-recording-control";

/**
 * Keeps session replay behind Cookiebot's statistics consent.
 *
 * Cookiebot cannot auto-block posthog-js — it only blocks `<script>` tags, and
 * we bundle PostHog from npm — so the gate has to be explicit. Recording starts
 * only once consent is confirmed and stops again if it is withdrawn, so
 * reopening the banner from the footer takes effect without a reload.
 *
 * Fails closed: with no Cookiebot on the page no event ever arrives and replay
 * stays off.
 */
const CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
] as const;

export function CookiebotConsentGate() {
  useEffect(() => {
    const syncConsent = () => {
      // Both hops are optional: `window.Cookiebot` appears only once the async
      // script loads, and `consent` is filled in a moment later still.
      setSessionRecordingConsent(
        window.Cookiebot?.consent?.statistics === true,
      );
    };

    // Cookiebot loads async and may have answered before this mounts, in which
    // case its events have already fired.
    syncConsent();

    for (const event of CONSENT_EVENTS) {
      window.addEventListener(event, syncConsent);
    }

    return () => {
      for (const event of CONSENT_EVENTS) {
        window.removeEventListener(event, syncConsent);
      }
    };
  }, []);

  return null;
}
